import Modal from '@Components/Shared/Modals/Modal';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilState, useRecoilValue } from 'recoil';
import { gtmPushEvent, sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import { useEffect, useState } from 'react';
import { subscriptionChannelType } from '@Components/Utilities/SandhillsConstants';
import { setLocalStorageValue, setSessionValue } from '@Components/Utilities/CookieHelper';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useRef } from 'react';

const QuickSignUpModal = (props) => {
    const currentSite = props.SiteName || '';
    const [email, setEmail] = useState(props.Email || '');
    const [emailSuggestion, setEmailSuggestion] = useState('');
    const [showThankYouModal, setShowThankYouModal] = useState(false);
    const [showInvalidEmail, setShowInvalidEmail] = useState(false);
    const [onScrollEvent, setOnScrollEvent] = useState(false);
    const routes = useRecoilValue(atoms.routes);
    const [LegalAgreementCheckbox, setLegalAgreementCheckbox] = useState(false);
    const [PixelsPolicyCheckbox, setPixelsPolicyCheckbox] = useState(false);
    const [showInvalidCheckbox, setShowInvalidCheckbox] = useState(false);
    const [addScrollEvent, setAddScrollEvent] = useState(false);
    const IsModalOpen = useRecoilValue(atoms.isModalOpen);
    const timeout = useRef(null);

    useEffect(() => {
        if (IsModalOpen) {
            clearTimeout(timeout.current);
            timeout.current = null;
        } else if (addScrollEvent) {
            displayQsuModalOnScroll();
        }
    }, [IsModalOpen]);

    useEffect(() => {
        if (props.DisplayOnScroll) {
            let dayMilliseconds = 1000 * 60 * 60 * 24;
            if (!hasClosedModal() || (((new Date().getTime() / dayMilliseconds) - (new Date(hasClosedModal()).getTime() / dayMilliseconds)) >= 90)) {
                if (props.Email) {
                    sandhillsRequest({
                        url: routes.CheckUserSubscriptionsURL,
                        method: 'post',
                        data: {
                            'sEmail': props.Email,
                            'sPhone': ''
                        },
                        errorHandling: {
                            useConsole: false,
                            useAlert: false
                        }
                    }).then((data) => {
                        if (!data[props.Email]) {
                            displayQsuModalOnScroll();
                        } else {
                            setLocalStorageModalClosed(true);
                        }
                    });
                } else {
                    displayQsuModalOnScroll();
                }
            }
        }
    }, []);

    const submitModal = () => {
        if (!props.IsDomesticSite && !LegalAgreementCheckbox) {
            setShowInvalidCheckbox(true);
        } else if (email) {
            validateEmail(email);
        }
    };

    const validateEmail = (email) => {
        sandhillsRequest({
            url: routes.ValidateEmail,
            method: 'post',
            data: {
                'sEmail': email
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response.DidYouMean) {
                setEmailSuggestion(response.DidYouMean);
            }

            if (response.IsValidEmail) {
                subsribeAndSubmit(email);
            } else {
                setShowInvalidEmail(true);
            }
        }).catch(() => {
            subsribeAndSubmit(email);
        });
    };

    const subsribeAndSubmit = (email) => {
        subscribeUser(email);
        setShowThankYouModal(true);
        setTimeout(() => {
            props.setShowModal(false);
            setShowThankYouModal(false);
        }, 3000);
        gtmPushEvent('quick-sign-up-success');
        setLocalStorageModalClosed(true);
    };

    const subscribeUser = (email) => {
        sandhillsRequest({
            url: routes.SubscribeUser,
            method: 'post',
            data: {
                user: email,
                subscriptionChannelTypeID: subscriptionChannelType.email,
                subscribe: true,
                webAgreementTypeIDs: props.WebAgreementTypeIDs,
                context: "WeeklyEmailUpdateFromQuickSignUpModal",
                PixelsPolicyAccepted: PixelsPolicyCheckbox
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        });
    };

    const handleClose = () => {
        props.setShowModal(false);
        gtmPushEvent('quick-sign-up-close');
        setLocalStorageModalClosed(onScrollEvent);
    };

    const handleSuggestionClick = () => {
        setShowInvalidEmail(false);
        setEmail(emailSuggestion);
        setEmailSuggestion('');
    };

    const displayQsuModalOnScroll = () => {
        let pageCount = updatePageLoadCount();
        if (pageCount >= 2) {
            setAddScrollEvent(true);
            window.addEventListener('scroll', () => scrollEvent(), { once: true });
        }
    };

    const updatePageLoadCount = () => {
        let pageLoadCount = sessionStorage.getItem('quickSignUpPageLoad');
        if (pageLoadCount === null) {
            pageLoadCount = 0;
        }
        pageLoadCount++;
        setSessionValue('quickSignUpPageLoad', pageLoadCount);
        return pageLoadCount;
    };

    const setLocalStorageModalClosed = (scroll) => {
        if (scroll) {
            setLocalStorageValue('quickSignUpClosed', new Date());
            setOnScrollEvent(false);
        }
    };

    const hasClosedModal = () => {
        return localStorage.getItem('quickSignUpClosed');
    };

    const scrollEvent = () => {
        if (!timeout.current && !hasClosedModal()) {
            timeout.current = setTimeout(() => {
                if (!hasClosedModal()) {
                    props.setShowModal(true);
                    setOnScrollEvent(true);
                    gtmPushEvent('quick-sign-up-open', 'qsuTriggerType', 'Scroll');
                }
            }, 15000);
        }
    };

    const onChange = (e) => {
        setEmail(e.target.value);
    };

    const onCheckboxChange = (e) => {
        setShowInvalidCheckbox(false)
        setLegalAgreementCheckbox(e.target.checked);
    };

    const onPixelsPolicyCheckboxChange = (e) => {
        setPixelsPolicyCheckbox(e.target.checked);
    };

    return (
        <Modal containerClassName='quick-signup' contentClassName='quick-signup-content' onEscapeKeyClose={handleClose}>
            {showThankYouModal ?
                <Modal.Header onClose={handleClose}>
                    <Modal.Title>
                        {translator.translate('sandhills.tradepubwebsite.mvc.thankyouforyoursubmission')}
                    </Modal.Title>
                </Modal.Header> :
                <>
                    <Modal.Header onClose={handleClose}>
                        <Modal.Title>
                            {props.IsRVUniverse ?
                                translator.translate('sandhills.tradesites.mvc.dreamingofyournextrv') :
                                translator.translate('sandhills.tradepubwebsite.mvc.getthelatest').replace('{0}', currentSite)
                            }
                        </Modal.Title>
                        {props.IsRVUniverse ?
                            <>
                                <h3>{translator.translate('sandhills.tradepubwebsite.mvc.getthelatest')}</h3>
                                <p>{translator.translate('sandhills.tradepubwebsite.mvc.newestlistings')} &nbsp; | &nbsp; {translator.translate('sandhills.tradepubwebsite.mvc.updates')} &nbsp; | &nbsp; {translator.translate('sandhills.tradepubwebsite.mvc.auctionevents')}</p>
                            </>
                            :
                            <p>{translator.translate('sandhills.tradepubwebsite.mvc.newestlistings')} &nbsp; | &nbsp; {translator.translate('sandhills.tradepubwebsite.mvc.updates')} &nbsp; | &nbsp; {translator.translate('sandhills.promotionsandspecialoffers')}</p>
                        }
                    </Modal.Header>
                    <Modal.Body>
                        <label id='qsuEmail' className='hidden'>{translator.translate('sandhills.enteryouremailaddress')}</label>
                        <input aria-required={true} aria-labelledby='qsuEmail' id='qsuUserEmail' type='text' placeholder={translator.translate('sandhills.enteryouremailaddress')} value={email} onChange={onChange} />
                        {showInvalidCheckbox &&
                            <div id='InvalidCheckbox' style={{ color: '#ff0000' }}>
                                <TranslationText tKey='sandhills.required' />
                            </div>}
                        {!props.IsDomesticSite &&
                            <>
                                <Grid item xs={12} sx={{ display: 'flex' }}>
                                    <Checkbox
                                        id='SubscribeToEmailSignUp'
                                        onChange={onCheckboxChange}
                                        checked={LegalAgreementCheckbox}

                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === 'Space') {
                                                onCheckboxChange({
                                                    target: {
                                                        id: 'SubscribeToEmailSignUp',
                                                        checked: !LegalAgreementCheckbox
                                                    }
                                                });
                                            }
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '14px', color: showInvalidCheckbox ? 'red' : '' }} dangerouslySetInnerHTML={{ __html: props.InternationalCheckboxStatement }} />
                                </Grid>
                                {props.IsFrenchSite && props.PixelsPolicyCheckboxStatement &&
                                    <Grid item xs={12} sx={{ display: 'flex' }}>
                                        <Checkbox
                                            id='PixelsPolicyAccepted'
                                            onChange={onPixelsPolicyCheckboxChange}
                                            checked={PixelsPolicyCheckbox}

                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === 'Space') {
                                                    onPixelsPolicyCheckboxChange({
                                                        target: {
                                                            id: 'PixelsPolicyAccepted',
                                                            checked: !PixelsPolicyCheckbox
                                                        }
                                                    });
                                                }
                                            }}
                                            inputProps={{
                                                'aria-labelledby': 'pixels-policy-consent'
                                            }}
                                        />
                                    <Typography id='pixels-policy-consent' sx={{ fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: props.PixelsPolicyCheckboxStatement }} />
                                    </Grid>
                                }
                                <Typography sx={{ fontSize: '.75em' }} dangerouslySetInnerHTML={{ __html: props.InternationalLegalStatement }} />
                            </>}

                        {props.IsDomesticSite && <Typography sx={{ fontSize: '.75em' }} dangerouslySetInnerHTML={{ __html: props.DomesticLegalStatement }} />}

                        <p id='description-label' className='hidden'>Sign up for Email updates from {currentSite}</p>
                        {showInvalidEmail &&
                            <div id='invalidEmail' style={{ color: '#ff0000' }}>
                                <TranslationText tKey='sandhills.registration.invalidemail' />
                            </div>}
                        {emailSuggestion &&
                            <div id='qsuEmailSuggestionWrapper' name='qsuEmailSuggestionWrapper' style={{ color: '#ff0000' }}>
                                <strong>{translator.translate('sandhills.registration.emailsuggesttext').replace('{0}?', ' ')}<span id='qsuEmailSuggestion' onClick={handleSuggestionClick} style={{ cursor: 'pointer', textDecoration: 'underline', color: 'black' }}>{emailSuggestion}</span>?</strong>
                            </div>}
                        <button className='button signup-btn ts-button' onClick={submitModal}><strong>{translator.translate('sandhills.tradepubwebsite.mvc.signupnow')}</strong></button>
                        <div role='button' tabIndex='0' className='signup-close'
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ')
                                {
                                    e.preventDefault();
                                    handleClose();
                                }
                            }} onClick={handleClose}><strong>{translator.translate('sandhills.tradepubwebsite.mvc.continueto').replace('{0}', currentSite)}</strong></div>
                    </Modal.Body>
                </>}
        </Modal>
    );
};

export default QuickSignUpModal;
global.QuickSignUpModal = QuickSignUpModal;